import React, { useState, useEffect } from 'react';
import { collection, getDocs, doc, getDoc } from 'firebase/firestore';
import { db } from 'firebaseConfig'; // Adjust the import path as needed
import Select from '@mui/material/Select';
import FormControl from '@mui/material/FormControl';
import MDBox from "components/MDBox"; // Assuming you have a custom MDBox component
import MDTypography from "components/MDTypography"; // Assuming you have a custom MDTypography component
import InputLabel from '@mui/material/InputLabel';
import Avatar from '@mui/material/Avatar';
import ListItemText from '@mui/material/ListItemText';
import Radio from '@mui/material/Radio';
import MDButton from "components/MDButton";
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingFlatIcon from '@mui/icons-material/TrendingFlat';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import { Card, Divider, MenuItem, Grid, Box, Modal } from '@mui/material';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Legend } from 'recharts';
import { Line } from 'react-chartjs-2';
import { useTheme } from '@mui/material/styles';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';


const StrategyDropdown = ({ userID }) => {
  const [strategies, setStrategies] = useState([]); // Stores { name: string, sessionIds: string[] }
  const [selectedStrategy1, setSelectedStrategy1] = useState('');
  const [selectedStrategy2, setSelectedStrategy2] = useState('');
  const [selectionMode, setSelectionMode] = useState('single');
  const [metrics1, setMetrics1] = useState(null);
  const [metrics2, setMetrics2] = useState(null);
  const [pnlData1, setPnlData1] = useState([]);
  const [pnlData2, setPnlData2] = useState([]);
  const [riskData1, setRiskData1] = useState([]);
  const [riskData2, setRiskData2] = useState([]);
  const [openModal, setOpenModal] = useState(false);
  const theme = useTheme();
  const iconColor = theme.palette.mode === 'dark' ? theme.palette.grey[200] : theme.palette.grey[600];

  const calculateMetrics = (trades, symbol) => {
    const totalTrades = trades.length;
    const totalPnL = trades.reduce((acc, trade) => acc + trade.profitLoss, 0);
    const wins = trades.filter(trade => trade.profitLoss > 0);
    const losses = trades.filter(trade => trade.profitLoss < 0);
    const breakevens = trades.filter(trade => trade.profitLoss === 0);
    const profitFactor = Math.min(wins.reduce((acc, trade) => acc + trade.profitLoss, 0) / Math.abs(losses.reduce((acc, trade) => acc + trade.profitLoss, 0)) || 1, 10);
    const winRate = totalTrades > 0 ? (wins.length / totalTrades) * 100 : 0;
    const averageRR = totalTrades > 0 ? trades.reduce((acc, trade) => acc + trade.rrTarget, 0) / totalTrades : 0;

    const totalHoldTime = trades.reduce((acc, trade, index) => {
      const entryDate = new Date(trade.currentPlaybackTime);
      const exitDate = new Date(trade.exitDate);
      if (!isNaN(entryDate.getTime()) && !isNaN(exitDate.getTime())) {
        const timeDiff = exitDate - entryDate;
        if (timeDiff >= 0 && timeDiff <= 24 * 60 * 60 * 1000) { // Cap at 24 hours
          return acc + timeDiff;
        } else {
          console.warn(`Invalid hold time for trade ${index}: ${timeDiff / (1000 * 60 * 60)} hours (entry: ${trade.currentPlaybackTime}, exit: ${trade.exitDate})`);
        }
      } else {
        console.warn(`Invalid date for trade ${index}: entry=${trade.currentPlaybackTime}, exit=${trade.exitDate}`);
      }
      return acc;
    }, 0);
    const averageHoldTime = totalTrades > 0 ? totalHoldTime / totalTrades / (1000 * 60 * 60) : 0; // Convert milliseconds to hours

    const bestSession = determineBestSession(wins);
    const bestSymbol = symbol;
    let peak = -Infinity;
    let maxDrawdown = 0;
    let cumulativePnL = 0;
    trades.forEach(trade => {
      cumulativePnL += trade.profitLoss;
      if (cumulativePnL > peak) {
        peak = cumulativePnL;
      }
      const drawdown = peak - cumulativePnL;
      if (drawdown > maxDrawdown) {
        maxDrawdown = drawdown;
      }
    });

    const expectancy = totalTrades > 0 ? totalPnL / totalTrades : 0;

    const tradesWithPnL = trades.map((trade, index) => ({
      ...trade,
      cumulativePnL: trades.slice(0, index + 1).reduce((acc, t) => acc + t.profitLoss, 0),
      date: new Date(trade.currentPlaybackTime).toLocaleString(),
    }));

    const averageRisk = totalTrades > 0 ? trades.reduce((acc, trade) => acc + trade.riskPercent, 0) / totalTrades : 0;
    const tradesWithTrailingStop = trades.filter(trade => trade.trailingStopLoss);
    const successfulTrailingStops = tradesWithTrailingStop.filter(trade => trade.profitLoss > 0);
    const trailingStopStats = {
      used: tradesWithTrailingStop.length,
      successRate: tradesWithTrailingStop.length > 0 ? (successfulTrailingStops.length / tradesWithTrailingStop.length) * 100 : 0,
      averageProfit: successfulTrailingStops.length > 0 ? successfulTrailingStops.reduce((acc, trade) => acc + trade.profitLoss, 0) / successfulTrailingStops.length : 0
    };

    const tradesWithPartialTP = trades.filter(trade => trade.partialTakeProfit);
    const successfulPartialTP = tradesWithPartialTP.filter(trade => trade.profitLoss > 0);
    const partialTPStats = {
      used: tradesWithPartialTP.length,
      successRate: tradesWithPartialTP.length > 0 ? (successfulPartialTP.length / tradesWithPartialTP.length) * 100 : 0,
      averageProfit: successfulPartialTP.length > 0 ? successfulPartialTP.reduce((acc, trade) => acc + trade.profitLoss, 0) / successfulPartialTP.length : 0
    };

    const tradesMovedToBE = trades.filter(trade => trade.isBreakEven);
    const successfulBEMoves = tradesMovedToBE.filter(trade => trade.profitLoss >= 0);
    const breakEvenStats = {
      used: tradesMovedToBE.length,
      successRate: tradesMovedToBE.length > 0 ? (successfulBEMoves.length / tradesMovedToBE.length) * 100 : 0,
      savedLoss: tradesMovedToBE.length > 0 ? tradesMovedToBE.reduce((acc, trade) => acc + Math.abs(trade.stopLoss - trade.price), 0) : 0
    };

    return {
      totalTrades,
      totalPnL,
      profitFactor,
      winRate,
      averageRR,
      averageHoldTime,
      totalWins: wins.length,
      totalLosses: losses.length,
      totalBreakevens: breakevens.length,
      bestSession,
      bestSymbol,
      maxDrawdown,
      expectancy,
      trades: tradesWithPnL,
      averageRisk,
      trailingStopStats,
      partialTPStats,
      breakEvenStats,
    };
  };

  const determineBestSession = (wins) => {
    const sessionCounts = { 'New York': 0, London: 0, Asia: 0 };
    wins.forEach(trade => {
      const entryDate = new Date(trade.currentPlaybackTime);
      const exitDate = new Date(trade.exitDate);
      if (!isNaN(entryDate.getTime()) && !isNaN(exitDate.getTime())) {
        const entryHour = entryDate.getUTCHours();
        const exitHour = exitDate.getUTCHours();
        if ((entryHour >= 12 && entryHour < 21) || (exitHour >= 12 && exitHour < 21)) {
          sessionCounts['New York']++;
        } else if ((entryHour >= 7 && entryHour < 16) || (exitHour >= 7 && exitHour < 16)) {
          sessionCounts.London++;
        } else {
          sessionCounts.Asia++;
        }
      }
    });
    return Object.keys(sessionCounts).reduce((a, b) => sessionCounts[a] > sessionCounts[b] ? a : b);
  };

  useEffect(() => {
    const fetchStrategies = async () => {
      const sessionRef = collection(db, 'Users', userID, 'backtesting');
      const sessionSnapshot = await getDocs(sessionRef);
      const strategyMap = {};
      sessionSnapshot.forEach(doc => {
        const data = doc.data();
        if (data.strategy && data.strategy !== 'N/A') {
          if (!strategyMap[data.strategy]) {
            strategyMap[data.strategy] = { name: data.strategy, sessionIds: [] };
          }
          strategyMap[data.strategy].sessionIds.push(doc.id);
        }
      });
      const strategyList = Object.values(strategyMap);
      setStrategies(strategyList);
    };
    fetchStrategies();
  }, [userID]);

  const fetchTradesData = async (strategyName, strategyNumber) => {
    const strategy = strategies.find(s => s.name === strategyName);
    if (!strategy) return;

    let allTrades = [];
    let symbol = null;
    for (const sessionId of strategy.sessionIds) {
      const tradesRef = collection(db, 'Users', userID, 'backtesting', sessionId, 'trades');
      const tradesSnapshot = await getDocs(tradesRef);
      const tradesList = tradesSnapshot.docs.map(doc => doc.data());
      allTrades = [...allTrades, ...tradesList];
      const sessionRef = doc(db, 'Users', userID, 'backtesting', sessionId);
      const sessionDoc = await getDoc(sessionRef);
      const sessionData = sessionDoc.data();
      if (sessionData) {
        symbol = sessionData.symbol; // Use last session's symbol; adjust if needed
      }
    }

    if (allTrades.length > 0 && symbol) {
      const metrics = calculateMetrics(allTrades, symbol);
      const pnlData = allTrades.map((trade, index) => ({
        date: new Date(trade.currentPlaybackTime).toLocaleString(),
        pnl: allTrades.slice(0, index + 1).reduce((acc, t) => acc + t.profitLoss, 0),
      }));
      const riskData = allTrades.map((trade, index) => ({
        date: new Date(trade.currentPlaybackTime).toLocaleString(),
        risk: trade.riskPercent,
      }));

      if (strategyNumber === 1) {
        setMetrics1(metrics);
        setPnlData1(pnlData);
        setRiskData1(riskData);
      } else {
        setMetrics2(metrics);
        setPnlData2(pnlData);
        setRiskData2(riskData);
      }
    } else {
      console.error('No trades or session data found for strategy:', strategyName);
    }
  };

  const pnlChartData = {
    labels: pnlData1.map(data => data.date) || [],
    datasets: [
      {
        label: `Strategy 1: ${strategies.find(strategy => strategy.name === selectedStrategy1)?.name || 'Strategy 1'}`,
        data: pnlData1.map(data => data.pnl) || [],
        fill: false,
        backgroundColor: 'rgb(0, 123, 255)',
        borderColor: 'rgba(0, 123, 255, 0.8)',
      },
      ...(selectionMode === 'multiple' && metrics2 ? [{
        label: `Strategy 2: ${strategies.find(strategy => strategy.name === selectedStrategy2)?.name || 'Strategy 2'}`,
        data: pnlData2.map(data => data.pnl) || [],
        fill: false,
        backgroundColor: 'rgb(220, 53, 69)',
        borderColor: 'rgba(220, 53, 69, 0.8)',
      }] : []),
    ],
  };

  const riskChartData = {
    labels: riskData1.map(data => data.date) || [],
    datasets: [
      {
        label: `Strategy 1: ${strategies.find(strategy => strategy.name === selectedStrategy1)?.name || 'Strategy 1'}`,
        data: riskData1.map(data => data.risk) || [],
        fill: false,
        backgroundColor: 'rgb(0, 123, 255)',
        borderColor: 'rgba(0, 123, 255, 0.8)',
      },
      ...(selectionMode === 'multiple' && metrics2 ? [{
        label: `Strategy 2: ${strategies.find(strategy => strategy.name === selectedStrategy2)?.name || 'Strategy 2'}`,
        data: riskData2.map(data => data.risk) || [],
        fill: false,
        backgroundColor: 'rgb(220, 53, 69)',
        borderColor: 'rgba(220, 53, 69, 0.8)',
      }] : []),
    ],
  };

  const pnlChartOptions = {
    maintainAspectRatio: false,
    responsive: true,
    scales: {
      x: {
        title: {
          display: true,
          text: 'Trades (PnL Chart)',
          font: {
            size: 16,
          },
        },
      },
      y: {
        beginAtZero: true,
        title: {
          display: true,
          text: 'PnL From Each Trade',
          font: {
            size: 16,
          },
        },
        ticks: {
          callback: function(value) {
            return '$' + value;
          },
        }
      },
    },
  };

  const riskChartOptions = {
    maintainAspectRatio: false,
    responsive: true,
    scales: {
      x: {
        title: {
          display: true,
          text: 'Trades (Risk Chart)',
          font: {
            size: 16,
          },
        },
      },
      y: {
        beginAtZero: true,
        title: {
          display: true,
          text: 'Risk Per Trade',
          font: {
            size: 16,
          },
        },
        ticks: {
          callback: function(value) {
            return '%' + value;
          }
        }
      },
    },
  };

  const handleChange1 = (event) => {
    const value = event.target.value;
    setSelectedStrategy1(value);
    fetchTradesData(value, 1);
  };

  const handleChange2 = (event) => {
    const value = event.target.value;
    setSelectedStrategy2(value);
    fetchTradesData(value, 2);
  };

  const handleModeChange = (event) => {
    setSelectionMode(event.target.value);
    setSelectedStrategy1('');
    setSelectedStrategy2('');
    setMetrics1(null);
    setMetrics2(null);
    setPnlData1([]);
    setPnlData2([]);
    setRiskData1([]);
    setRiskData2([]);
  };

  const handleOpenModal = () => setOpenModal(true);
  const handleCloseModal = () => setOpenModal(false);

  const modalStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '800px',
    height: '900px',
    backgroundColor: theme.palette.background.default || '#bac7db',
    border: '2px solid #000',
    boxShadow: 24,
    p: 4,
    overflowY: 'auto',
  };

  const generateReport = (metrics1, metrics2) => {
    const strategy1Name = strategies.find(strategy => strategy.name === selectedStrategy1)?.name || 'Strategy 1';
    const strategy2Name = strategies.find(strategy => strategy.name === selectedStrategy2)?.name || 'Strategy 2';

    const analyzeTradingTechniques = () => {
      const analysis = [];
      if (metrics1.trailingStopStats.used > 0 || metrics2.trailingStopStats.used > 0) {
        analysis.push(
          <MDTypography key="trailing-title" variant="h5" align="center" gutterBottom>
            Trailing Stop Loss Usage
          </MDTypography>,
          <MDTypography key="trailing-body" variant="body2" paragraph>
            {strategy1Name} used trailing stops in {metrics1.trailingStopStats.used} trades. Of these trades, {metrics1.trailingStopStats.successRate.toFixed(1)}% ended in profit.
            {strategy2Name} used trailing stops in {metrics2.trailingStopStats.used} trades. Of these trades, {metrics2.trailingStopStats.successRate.toFixed(1)}% ended in profit.
          </MDTypography>
        );
      }
      if (metrics1.partialTPStats.used > 0 || metrics2.partialTPStats.used > 0) {
        analysis.push(
          <MDTypography key="partial-title" variant="h5" align="center" gutterBottom>
            Partial Take Profit Usage
          </MDTypography>,
          <MDTypography key="partial-body" variant="body2" paragraph>
            {strategy1Name} used partial take profits in {metrics1.partialTPStats.used} trades. Of these trades, {metrics1.partialTPStats.successRate.toFixed(1)}% ended in profit.
            {strategy2Name} used partial take profits in {metrics2.partialTPStats.used} trades. Of these trades, {metrics2.partialTPStats.successRate.toFixed(1)}% ended in profit.
          </MDTypography>
        );
      }
      if (metrics1.breakEvenStats.used > 0 || metrics2.breakEvenStats.used > 0) {
        analysis.push(
          <MDTypography key="breakeven-title" variant="h5" align="center" gutterBottom>
            Break Even Usage
          </MDTypography>,
          <MDTypography key="breakeven-body" variant="body2" paragraph>
            {strategy1Name} moved {metrics1.breakEvenStats.used} trades to break even. Of these trades, {metrics1.breakEvenStats.successRate.toFixed(1)}% avoided a loss.
            {strategy2Name} moved {metrics2.breakEvenStats.used} trades to break even. Of these trades, {metrics2.breakEvenStats.successRate.toFixed(1)}% avoided a loss.
          </MDTypography>
        );
      }
      analysis.push(
        <MDTypography key="note" variant="caption" color="text.secondary" paragraph>
          Note: These statistics show only the number of trades where each tool was used and their outcomes. 
          The direct impact of these tools on trade performance cannot be determined without additional user analysis.
        </MDTypography>
      );
      return analysis;
    };

    const generateSummary = (metrics1, metrics2, strategy1Name, strategy2Name) => {
      const summary = [];
      const betterMetrics1 = [];
      const betterMetrics2 = [];
      const compareMetric = (metric, name, higherIsBetter = true, threshold = 0) => {
        const val1 = metrics1[metric];
        const val2 = metrics2[metric];
        if (Math.abs(val1 - val2) <= threshold) return;
        if ((val1 > val2 && higherIsBetter) || (val1 < val2 && !higherIsBetter)) {
          betterMetrics1.push(name);
        } else {
          betterMetrics2.push(name);
        }
      };

      compareMetric('totalTrades', 'total trades');
      compareMetric('totalPnL', 'total PnL');
      compareMetric('profitFactor', 'profit factor');
      compareMetric('winRate', 'win rate');
      compareMetric('averageRR', 'average risk/reward ratio');
      compareMetric('averageHoldTime', 'average hold time', false);
      compareMetric('maxDrawdown', 'max drawdown', false);
      compareMetric('expectancy', 'expectancy');
      compareMetric('averageRisk', 'average risk', false);

      if (betterMetrics1.length > betterMetrics2.length) {
        summary.push(
          `Based on the analysis, ${strategy1Name} demonstrates stronger performance across most metrics, including ${betterMetrics1.join(', ')}. This suggests that ${strategy1Name} is potentially more reliable for achieving a consistent profit with controlled risk.`
        );
      } else if (betterMetrics2.length > betterMetrics1.length) {
        summary.push(
          `Based on the analysis, ${strategy2Name} shows superior performance across most metrics, particularly in areas like ${betterMetrics2.join(', ')}. This indicates that ${strategy2Name} may be a more effective choice for steady gains while managing risk effectively.`
        );
      } else {
        summary.push(
          `Both strategies have unique strengths. ${strategy1Name} performs better in ${betterMetrics1.join(', ')}, while ${strategy2Name} excels in ${betterMetrics2.join(', ')}. The choice depends on the specific risk and profit preferences.`
        );
      }

      if (metrics1.totalPnL < 0) {
        summary.push(`${strategy1Name} has a negative total PnL, indicating it has lost money overall.`);
      }
      if (metrics2.totalPnL < 0) {
        summary.push(`${strategy2Name} has a negative total PnL, indicating it has lost money overall.`);
      }

      if (metrics1.winRate < 50 && metrics1.profitFactor < 1) {
        summary.push(`${strategy1Name} has a low win rate and a poor profit factor, indicating it is not a profitable strategy.`);
      }
      if (metrics2.winRate < 50 && metrics2.profitFactor < 1) {
        summary.push(`${strategy2Name} has a low win rate and a poor profit factor, indicating it is not a profitable strategy.`);
      }

      if (metrics1.averageRisk > 5) {
        summary.push(`${strategy1Name} takes on high risk per trade, which may not be suitable for risk-averse traders.`);
      }
      if (metrics2.averageRisk > 5) {
        summary.push(`${strategy2Name} takes on high risk per trade, which may not be suitable for risk-averse traders.`);
      }

      if (metrics1.maxDrawdown < metrics2.maxDrawdown) {
        summary.push(`${strategy1Name} has a lower maximum drawdown, suggesting a more conservative risk management approach.`);
      } else if (metrics2.maxDrawdown < metrics1.maxDrawdown) {
        summary.push(`${strategy2Name} shows a lower maximum drawdown, which may be preferable for risk-sensitive strategies.`);
      }

      if (metrics1.averageHoldTime < metrics2.averageHoldTime) {
        summary.push(`${strategy1Name} has a shorter average hold time, which could be advantageous for strategies aiming for quick trades.`);
      } else if (metrics2.averageHoldTime < metrics1.averageHoldTime) {
        summary.push(`${strategy2Name} has a shorter average hold time, beneficial for strategies focused on short-term gains.`);
      }

      if (metrics1.profitFactor > 1 && metrics2.profitFactor > 1) {
        summary.push(`Both strategies are profitable overall, with profit factors above 1.`);
      } else if (metrics1.profitFactor > 1) {
        summary.push(`${strategy1Name} is profitable overall, while ${strategy2Name} may need adjustments to increase its profit factor.`);
      } else if (metrics2.profitFactor > 1) {
        summary.push(`${strategy2Name} is profitable overall, while ${strategy1Name} may need refinement to improve profitability.`);
      }

      if (metrics1.winRate > metrics2.winRate) {
        summary.push(`${strategy1Name} has a higher win rate, which may appeal to traders prioritizing consistency.`);
      } else if (metrics2.winRate > metrics1.winRate) {
        summary.push(`${strategy2Name} has a higher win rate, suggesting it may be more reliable in terms of profitable trades.`);
      }

      if (metrics1.expectancy > metrics2.expectancy) {
        summary.push(`With a higher return expectancy, ${strategy1Name} has the potential for greater returns per trade.`);
      } else if (metrics2.expectancy > metrics1.expectancy) {
        summary.push(`With a higher return expectancy, ${strategy2Name} may yield higher average returns per trade.`);
      }

      let dynamicConclusion = '';
      if (betterMetrics1.length > betterMetrics2.length) {
        dynamicConclusion = `Therefore, it appears as though ${strategy1Name} performs better overall based on these metrics.`;
      } else if (betterMetrics2.length > betterMetrics1.length) {
        dynamicConclusion = `Therefore, it appears as though ${strategy2Name} performs better overall based on these metrics.`;
      } else {
        dynamicConclusion = `Therefore, it appears as though both strategies perform equally well overall based on these metrics.`;
      }

      const disclaimer = (
        <em>
          <br />
          *This analysis is not financial advice and should not be considered as a recommendation. Past performance is not indicative of future results.*
        </em>
      );

      return (
        <>
          <p>{summary.join(' ')}</p>
          <p>{dynamicConclusion}</p>
          {disclaimer}
          {analyzeTradingTechniques()}
        </>
      );
    };

    return (
      <MDBox mt={3}>
        <MDTypography variant="h4" align="center" gutterBottom>
          AI Enhanced Comparison Report
        </MDTypography>
        <MDTypography variant="h5" align="center" gutterBottom>
          Total Trades
        </MDTypography>
        <MDTypography variant="body1">
          <strong>{strategy1Name}:</strong> {metrics1.totalTrades} trades<br />
          <strong>{strategy2Name}:</strong> {metrics2.totalTrades} trades
        </MDTypography>
        <MDTypography variant="body2" paragraph>
          <em>Analysis:</em> {metrics1.totalTrades === metrics2.totalTrades ? `${strategy1Name} and ${strategy2Name} have the same total trades, indicating similar performance in this aspect.` : `${strategy1Name} has ${metrics1.totalTrades > metrics2.totalTrades ? 'more' : 'fewer'} trades than ${strategy2Name}, indicating ${metrics1.totalTrades > metrics2.totalTrades ? 'higher trading activity' : 'lower trading activity'}. This could suggest that ${strategy1Name} is more aggressive or opportunistic, while ${strategy2Name} might be more selective.`}
        </MDTypography>
        <MDTypography variant="h5" align="center" gutterBottom>
          Profit and Loss (PnL)
        </MDTypography>
        <MDTypography variant="body1">
          <strong>{strategy1Name} Total PnL:</strong> ${metrics1.totalPnL.toLocaleString()}<br />
          <strong>{strategy2Name} Total PnL:</strong> ${metrics2.totalPnL.toLocaleString()}
        </MDTypography>
        <MDTypography variant="body2" paragraph>
          <em>Analysis:</em> {metrics1.totalPnL === metrics2.totalPnL ? `${strategy1Name} and ${strategy2Name} have the same total PnL, indicating similar performance in this aspect.` : `${metrics2.totalPnL > metrics1.totalPnL ? `${strategy2Name} has a higher total PnL compared to ${strategy1Name}, suggesting better profitability.` : `${strategy1Name} has a higher total PnL compared to ${strategy2Name}, suggesting better profitability.`}`}
          {metrics1.totalPnL < 0 && <MDTypography variant="body2" paragraph>{strategy1Name} has a negative total PnL, indicating it has lost money overall.</MDTypography>}
          {metrics2.totalPnL < 0 && <MDTypography variant="body2" paragraph>{strategy2Name} has a negative total PnL, indicating it has lost money overall.</MDTypography>}
        </MDTypography>
        <MDTypography variant="h5" align="center" gutterBottom>
          Profit Factor
        </MDTypography>
        <MDTypography variant="body1">
          <strong>{strategy1Name} Profit Factor:</strong> {metrics1.profitFactor.toFixed(2)}<br />
          <strong>{strategy2Name} Profit Factor:</strong> {metrics2.profitFactor.toFixed(2)}
        </MDTypography>
        <MDTypography variant="body2" paragraph>
          <em>Analysis:</em> {metrics1.profitFactor === metrics2.profitFactor ? `${strategy1Name} and ${strategy2Name} have the same profit factor.` : `${metrics2.profitFactor > metrics1.profitFactor ? `${strategy2Name} has a higher profit factor, indicating better profitability per unit of risk.` : `${strategy1Name} has a higher profit factor, indicating better profitability per unit of risk.`}`}
          {metrics1.profitFactor < 1 && <MDTypography variant="body2" paragraph>{strategy1Name} has a poor profit factor, indicating it is not a profitable strategy.</MDTypography>}
          {metrics2.profitFactor < 1 && <MDTypography variant="body2" paragraph>{strategy2Name} has a poor profit factor, indicating it is not a profitable strategy.</MDTypography>}
        </MDTypography>
        <MDTypography variant="h5" align="center" gutterBottom>
          Win Rate
        </MDTypography>
        <MDTypography variant="body1">
          <strong>{strategy1Name} Win Rate:</strong> {metrics1.winRate.toFixed(2)}%<br />
          <strong>{strategy2Name} Win Rate:</strong> {metrics2.winRate.toFixed(2)}%
        </MDTypography>
        <MDTypography variant="body2" paragraph>
          <em>Analysis:</em> {metrics1.winRate === metrics2.winRate ? `${strategy1Name} and ${strategy2Name} have the same win rate.` : `${metrics2.winRate > metrics1.winRate ? `${strategy2Name} has a higher win rate, indicating more consistent profitability.` : `${strategy1Name} has a higher win rate, indicating more consistent profitability.`}`}
          {metrics1.winRate < 50 && metrics1.profitFactor < 1 && <MDTypography variant="body2" paragraph>{strategy1Name} has a low win rate and a poor profit factor, indicating it is not a profitable strategy.</MDTypography>}
          {metrics2.winRate < 50 && metrics2.profitFactor < 1 && <MDTypography variant="body2" paragraph>{strategy2Name} has a low win rate and a poor profit factor, indicating it is not a profitable strategy.</MDTypography>}
        </MDTypography>
        <MDTypography variant="h5" align="center" gutterBottom>
          Average Risk/Reward Ratio
        </MDTypography>
        <MDTypography variant="body1">
          <strong>{strategy1Name} Average RR:</strong> {metrics1.averageRR.toFixed(2)}<br />
          <strong>{strategy2Name} Average RR:</strong> {metrics2.averageRR.toFixed(2)}
        </MDTypography>
        <MDTypography variant="body2" paragraph>
          <em>Analysis:</em> {metrics1.averageRR === metrics2.averageRR ? `${strategy1Name} and ${strategy2Name} have the same average RR.` : `${metrics2.averageRR > metrics1.averageRR ? `${strategy2Name} has a higher average RR, indicating better potential for reward relative to risk.` : `${strategy1Name} has a higher average RR, indicating better potential for reward relative to risk.`}`}
        </MDTypography>
        <MDTypography variant="h5" align="center" gutterBottom>
          Average Hold Time
        </MDTypography>
        <MDTypography variant="body1">
          <strong>{strategy1Name} Hold Time:</strong> {metrics1.averageHoldTime.toFixed(2)} hours<br />
          <strong>{strategy2Name} Hold Time:</strong> {metrics2.averageHoldTime.toFixed(2)} hours
        </MDTypography>
        <MDTypography variant="body2" paragraph>
          <em>Analysis:</em> {metrics1.averageHoldTime === metrics2.averageHoldTime ? `${strategy1Name} and ${strategy2Name} have the same average hold time.` : `${metrics2.averageHoldTime > metrics1.averageHoldTime ? `${strategy2Name} holds positions longer on average.` : `${strategy1Name} holds positions longer on average.`}`}
        </MDTypography>
        <MDTypography variant="h5" align="center" gutterBottom>
          Best Session
        </MDTypography>
        <MDTypography variant="body1">
          <strong>{strategy1Name} Best Session:</strong> {metrics1.bestSession}<br />
          <strong>{strategy2Name} Best Session:</strong> {metrics2.bestSession}
        </MDTypography>
        <MDTypography variant="body2" paragraph>
          <em>Analysis:</em> {strategy1Name} performed best during the {metrics1.bestSession} session, while {strategy2Name} excelled in the {metrics2.bestSession} session.
        </MDTypography>
        <MDTypography variant="h5" align="center" gutterBottom>
          Max Drawdown
        </MDTypography>
        <MDTypography variant="body1">
          <strong>{strategy1Name} Max Drawdown:</strong> ${metrics1.maxDrawdown.toLocaleString()}<br />
          <strong>{strategy2Name} Max Drawdown:</strong> ${metrics2.maxDrawdown.toLocaleString()}
        </MDTypography>
        <MDTypography variant="body2" paragraph>
          <em>Analysis:</em> {metrics1.maxDrawdown === metrics2.maxDrawdown ? `${strategy1Name} and ${strategy2Name} have the same max drawdown.` : `${metrics2.maxDrawdown < metrics1.maxDrawdown ? `${strategy2Name} has a lower max drawdown, indicating more consistent performance.` : `${strategy1Name} has a lower max drawdown, indicating more consistent performance.`}`}
        </MDTypography>
        <MDTypography variant="h5" align="center" gutterBottom>
          Expectancy
        </MDTypography>
        <MDTypography variant="body1">
          <strong>{strategy1Name} Expectancy:</strong> ${metrics1.expectancy.toFixed(2)} per trade<br />
          <strong>{strategy2Name} Expectancy:</strong> ${metrics2.expectancy.toFixed(2)} per trade
        </MDTypography>
        <MDTypography variant="body2" paragraph>
          <em>Analysis:</em> {metrics1.expectancy === metrics2.expectancy ? `${strategy1Name} and ${strategy2Name} have the same expectancy.` : `${metrics2.expectancy > metrics1.expectancy ? `${strategy2Name} provides a higher expected return per trade.` : `${strategy1Name} provides a higher expected return per trade.`}`}
        </MDTypography>
        <MDTypography variant="h5" align="center" gutterBottom>
          Average Risk (%)
        </MDTypography>
        <MDTypography variant="body1">
          <strong>{strategy1Name} Average Risk:</strong> {metrics1.averageRisk.toFixed(2)}%<br />
          <strong>{strategy2Name} Average Risk:</strong> {metrics2.averageRisk.toFixed(2)}%
        </MDTypography>
        <MDTypography variant="body2" paragraph>
          <em>Analysis:</em> {metrics1.averageRisk === metrics2.averageRisk ? `${strategy1Name} and ${strategy2Name} have the same average risk.` : `${metrics2.averageRisk < metrics1.averageRisk ? `${strategy2Name} takes on less risk per trade on average.` : `${strategy1Name} takes on less risk per trade on average.`}`}
          {metrics1.averageRisk > 5 && <MDTypography variant="body2" paragraph>{strategy1Name} takes on high risk per trade, which may not be suitable for risk-averse traders.</MDTypography>}
          {metrics2.averageRisk > 5 && <MDTypography variant="body2" paragraph>{strategy2Name} takes on high risk per trade, which may not be suitable for risk-averse traders.</MDTypography>}
        </MDTypography>
        <MDTypography variant="h5" align="center" gutterBottom>
          Summary
        </MDTypography>
        <MDTypography variant="body2">
          {generateSummary(metrics1, metrics2, strategy1Name, strategy2Name)}
        </MDTypography>
      </MDBox>
    );
  };

  return (
    <Card style={{ minHeight: '200px' }}>
      <MDBox display="flex" flexDirection="column" justifyContent="space-between" pt={1} px={2} height="100%">
        <MDBox display="flex" justifyContent="space-between">
          <MDBox
            variant="gradient"
            bgColor="light"
            color="dark"
            coloredShadow="light"
            borderRadius="xl"
            display="flex"
            justifyContent="center"
            alignItems="center"
            width="4rem"
            height="4rem"
            mt={-3}
          >
            <Avatar variant="rounded">
              <AnalyticsIcon fontSize="large" color="dark" />
            </Avatar>
          </MDBox>
          <MDBox textAlign="right" lineHeight={1.25}>
            <MDTypography variant="button" fontWeight="light" color="text" fontFamily="inherit">
              FX Bullsheet Backtesting - Strategy Analytics
            </MDTypography>
          </MDBox>
        </MDBox>
        <Divider />
        <MDBox display="flex" flexDirection="column" pb={2} px={2} alignItems="center" justifyContent="center">
          <MDBox mt={1}>
            <MDTypography variant="h4" textAlign="center">Select Analysis Mode</MDTypography>
            <RadioGroup row value={selectionMode} onChange={handleModeChange}>
              <FormControlLabel value="single" control={<Radio />} label="Single Strategy" />
              <FormControlLabel value="multiple" control={<Radio />} label="Compare Strategies" />
            </RadioGroup>
          </MDBox>
          <MDBox display="flex" flexDirection="row" justifyContent="center" mt={2}>
            {strategies.length === 0 ? (
              <MDTypography variant="body1" fontSize="16px">
                No strategies available. Please define a backtesting session with a strategy.
              </MDTypography>
            ) : (
              <>
                <FormControl variant="outlined" sx={{ m: 1, minWidth: 120, width: '200px' }}>
                  <InputLabel id="strategy-select-label">Select Strategy</InputLabel>
                  <Select
                    labelId="strategy-select-label"
                    value={selectedStrategy1}
                    onChange={handleChange1}
                    label="Select Strategy"
                    sx={{
                      '& .MuiOutlinedInput-notchedOutline': { borderColor: 'white' },
                      '& legend': { maxWidth: '100%' },
                    }}
                  >
                    <MenuItem value="" disabled>
                      <ListItemText primary="e.g., Moving Average Crossover" />
                    </MenuItem>
                    {strategies.map((strategy) => (
                      <MenuItem key={strategy.name} value={strategy.name}>
                        <ListItemText primary={strategy.name} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {selectionMode === 'multiple' && (
                  <FormControl variant="outlined" sx={{ m: 1, minWidth: 120, width: '200px' }}>
                    <InputLabel id="strategy-select-label-2">Select Strategy to Compare</InputLabel>
                    <Select
                      labelId="strategy-select-label-2"
                      value={selectedStrategy2}
                      onChange={handleChange2}
                      label="Select Strategy to Compare"
                      sx={{
                        '& .MuiOutlinedInput-notchedOutline': { borderColor: 'white' },
                        '& legend': { maxWidth: '100%' },
                      }}
                    >
                      <MenuItem value="" disabled>
                        <ListItemText primary="e.g., Bollinger Bands" />
                      </MenuItem>
                      {strategies.map((strategy) => (
                        <MenuItem key={strategy.name} value={strategy.name}>
                          <ListItemText primary={strategy.name} />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              </>
            )}
          </MDBox>
        </MDBox>
        {(metrics1 && (selectionMode === 'single' || (selectionMode === 'multiple' && metrics2))) && (
          <MDBox mt={3}>
            <Grid container spacing={3}>
            {selectionMode === 'multiple' && (
            <>
                <Grid item xs={12} textAlign="center">
                <MDButton variant="contained" color="warning" onClick={handleOpenModal}>
                    Generate AI Comparison Report
                </MDButton>
                </Grid>
                  <Grid item xs={12} md={6}>
                    <MDTypography variant="h5" fontWeight="medium" textAlign="center">
                      Strategy 1: {strategies.find(strategy => strategy.id === selectedStrategy1)?.name}
                    </MDTypography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <MDTypography variant="h5" fontWeight="medium" textAlign="center">
                      Strategy 2: {strategies.find(strategy => strategy.id === selectedStrategy2)?.name}
                    </MDTypography>
                  </Grid>
                </>
              )}
              <Grid item xs={12} md={selectionMode === 'single' ? 3 : 6}>
                <Card style={{ minHeight: '100px' }}>
                  <MDBox display="flex" flexDirection="column" justifyContent="center" alignItems="center" pt={2} px={2} height="100%">
                    <MDTypography variant="h6" fontWeight="medium">
                      Total Trades
                    </MDTypography>
                    <MDTypography variant="h4" fontWeight="medium">
                      {metrics1.totalTrades}
                    </MDTypography>
                    <MDBox display="flex" flexDirection="row" justifyContent="space-around" width="100%" mt={0.5}>
                      <MDTypography variant="h4" fontSize="16px">
                        Wins: <span style={{ fontWeight: 'bold', color: 'green' }}>{metrics1.totalWins}</span>
                      </MDTypography>
                      <MDTypography variant="h4" fontSize="16px">
                        Losses: <span style={{ fontWeight: 'bold', color: 'red' }}>{metrics1.totalLosses}</span>
                      </MDTypography>
                      <MDTypography variant="h4" fontSize="16px">
                        Breakevens: <span style={{ fontWeight: 'bold', color: 'orange' }}>{metrics1.totalBreakevens}</span>
                      </MDTypography>
                    </MDBox>
                  </MDBox>
                </Card>
              </Grid>
              {selectionMode === 'multiple' && (
                <>

                  <Grid item xs={12} md={6}>
                    <Card style={{ minHeight: '100px' }}>
                      <MDBox display="flex" flexDirection="column" justifyContent="center" alignItems="center" pt={2} px={2} height="100%">
                        <MDTypography variant="h6" fontWeight="medium">
                          Total Trades
                        </MDTypography>
                        <MDTypography variant="h4" fontWeight="medium">
                          {metrics2.totalTrades}
                        </MDTypography>
                        <MDBox display="flex" flexDirection="row" justifyContent="space-around" width="100%" mt={0.5}>
                          <MDTypography variant="h4" fontSize="16px">
                            Wins: <span style={{ fontWeight: 'bold', color: 'green' }}>{metrics2.totalWins}</span>
                          </MDTypography>
                          <MDTypography variant="h4" fontSize="16px">
                            Losses: <span style={{ fontWeight: 'bold', color: 'red' }}>{metrics2.totalLosses}</span>
                          </MDTypography>
                          <MDTypography variant="h4" fontSize="16px">
                            Breakevens: <span style={{ fontWeight: 'bold', color: 'orange' }}>{metrics2.totalBreakevens}</span>
                          </MDTypography>
                        </MDBox>
                      </MDBox>
                    </Card>
                  </Grid>
                </>
              )}
              <Grid item xs={12} md={selectionMode === 'single' ? 3 : 6}>
                <Card style={{ minHeight: '100px' }}>
                  <MDBox display="flex" flexDirection="column" justifyContent="center" alignItems="center" pt={2} px={2} height="100%">
                    <MDTypography variant="h6" fontWeight="medium">
                      Total PnL
                    </MDTypography>
                    <MDTypography variant="h4" fontWeight="bold">
                      ${metrics1.totalPnL.toFixed(2)}
                    </MDTypography>
                  </MDBox>
                </Card>
              </Grid>
              {selectionMode === 'multiple' && metrics2 && (
                <Grid item xs={12} md={6}>
                  <Card style={{ minHeight: '100px' }}>
                    <MDBox display="flex" flexDirection="column" justifyContent="center" alignItems="center" pt={2} px={2} height="100%">
                      <MDTypography variant="h6" fontWeight="medium">
                        Total PnL
                      </MDTypography>
                      <MDTypography variant="h4" fontWeight="bold">
                        ${metrics2.totalPnL.toFixed(2)}
                      </MDTypography>
                    </MDBox>
                  </Card>
                </Grid>
              )}
              <Grid item xs={12} md={selectionMode === 'single' ? 3 : 6}>
                <Card style={{ minHeight: '100px' }}>
                  <MDBox display="flex" flexDirection="column" justifyContent="center" alignItems="center" pt={2} px={2} height="100%">
                    <MDTypography variant="h6" fontWeight="medium">
                      Average RR
                    </MDTypography>
                    <MDTypography variant="h4" fontWeight="bold">
                      {metrics1.averageRR.toFixed(2)}
                    </MDTypography>
                  </MDBox>
                </Card>
              </Grid>
              {selectionMode === 'multiple' && metrics2 && (
                <Grid item xs={12} md={6}>
                  <Card style={{ minHeight: '100px' }}>
                    <MDBox display="flex" flexDirection="column" justifyContent="center" alignItems="center" pt={2} px={2} height="100%">
                      <MDTypography variant="h6" fontWeight="medium">
                        Average RR
                      </MDTypography>
                      <MDTypography variant="h4" fontWeight="bold">
                        {metrics2.averageRR.toFixed(2)}
                      </MDTypography>
                    </MDBox>
                  </Card>
                </Grid>
              )}
              <Grid item xs={12} md={selectionMode === 'single' ? 3 : 6}>
                <Card style={{ minHeight: '100px' }}>
                  <MDBox display="flex" flexDirection="column" justifyContent="center" alignItems="center" pt={2} px={2} height="100%">
                    <MDTypography variant="h6" fontWeight="medium">
                      Average Hold Time
                    </MDTypography>
                    <MDTypography variant="h4" fontWeight="bold">
                      {metrics1.averageHoldTime.toFixed(2)} hrs
                    </MDTypography>
                  </MDBox>
                </Card>
              </Grid>
              {selectionMode === 'multiple' && metrics2 && (
                <Grid item xs={12} md={6}>
                  <Card style={{ minHeight: '100px' }}>
                    <MDBox display="flex" flexDirection="column" justifyContent="center" alignItems="center" pt={2} px={2} height="100%">
                      <MDTypography variant="h6" fontWeight="medium">
                        Average Hold Time
                      </MDTypography>
                      <MDTypography variant="h4" fontWeight="bold">
                        {metrics2.averageHoldTime.toFixed(2)} hrs
                      </MDTypography>
                    </MDBox>
                  </Card>
                </Grid>
              )}
              <Grid item xs={12} md={selectionMode === 'single' ? 3 : 6}>
                <Card style={{ minHeight: '100px' }}>
                  <MDBox display="flex" flexDirection="column" justifyContent="center" alignItems="center" pt={2} px={2} height="100%">
                    <MDTypography variant="h6" fontWeight="medium">
                      Best Session
                    </MDTypography>
                    <MDTypography variant="h4" fontWeight="bold">
                      {metrics1.bestSession}
                    </MDTypography>
                  </MDBox>
                </Card>
              </Grid>
              {selectionMode === 'multiple' && metrics2 && (
                <Grid item xs={12} md={6}>
                  <Card style={{ minHeight: '100px' }}>
                    <MDBox display="flex" flexDirection="column" justifyContent="center" alignItems="center" pt={2} px={2} height="100%">
                      <MDTypography variant="h6" fontWeight="medium">
                        Best Session
                      </MDTypography>
                      <MDTypography variant="h4" fontWeight="bold">
                        {metrics2.bestSession}
                      </MDTypography>
                    </MDBox>
                  </Card>
                </Grid>
              )}
              <Grid item xs={12} md={selectionMode === 'single' ? 3 : 6}>
                <Card style={{ minHeight: '100px' }}>
                  <MDBox display="flex" flexDirection="column" justifyContent="center" alignItems="center" pt={2} px={2} height="100%">
                    <MDTypography variant="h6" fontWeight="medium">
                      Best Symbol
                    </MDTypography>
                    <MDTypography variant="h4" fontWeight="bold">
                      {metrics1.bestSymbol}
                    </MDTypography>
                  </MDBox>
                </Card>
              </Grid>
              {selectionMode === 'multiple' && metrics2 && (
                <Grid item xs={12} md={6}>
                  <Card style={{ minHeight: '100px' }}>
                    <MDBox display="flex" flexDirection="column" justifyContent="center" alignItems="center" pt={2} px={2} height="100%">
                      <MDTypography variant="h6" fontWeight="medium">
                        Best Symbol
                      </MDTypography>
                      <MDTypography variant="h4" fontWeight="bold">
                        {metrics2.bestSymbol}
                      </MDTypography>
                    </MDBox>
                  </Card>
                </Grid>
              )}
              <Grid item xs={12} md={selectionMode === 'single' ? 3 : 6}>
                <Card style={{ minHeight: '100px' }}>
                  <MDBox display="flex" flexDirection="column" justifyContent="center" alignItems="center" pt={2} px={2} height="100%">
                    <MDTypography variant="h6" fontWeight="medium">
                      Max Drawdown
                    </MDTypography>
                    <MDTypography variant="h4" fontWeight="bold">
                      ${metrics1.maxDrawdown.toFixed(2)}
                    </MDTypography>
                  </MDBox>
                </Card>
              </Grid>
              {selectionMode === 'multiple' && metrics2 && (
                <Grid item xs={12} md={6}>
                  <Card style={{ minHeight: '100px' }}>
                    <MDBox display="flex" flexDirection="column" justifyContent="center" alignItems="center" pt={2} px={2} height="100%">
                      <MDTypography variant="h6" fontWeight="medium">
                        Max Drawdown
                      </MDTypography>
                      <MDTypography variant="h4" fontWeight="bold">
                        ${metrics2.maxDrawdown.toFixed(2)}
                      </MDTypography>
                    </MDBox>
                  </Card>
                </Grid>
              )}
              <Grid item xs={12} md={selectionMode === 'single' ? 3 : 6}>
                <Card style={{ minHeight: '100px' }}>
                  <MDBox display="flex" flexDirection="column" justifyContent="center" alignItems="center" pt={2} px={2} height="100%">
                    <MDTypography variant="h6" fontWeight="medium">
                      Average Profit Per Trade
                    </MDTypography>
                    <MDTypography variant="h4" fontWeight="bold">
                      ${metrics1.expectancy.toFixed(2)}
                    </MDTypography>
                  </MDBox>
                </Card>
              </Grid>
              {selectionMode === 'multiple' && metrics2 && (
                <Grid item xs={12} md={6}>
                  <Card style={{ minHeight: '100px' }}>
                    <MDBox display="flex" flexDirection="column" justifyContent="center" alignItems="center" pt={2} px={2} height="100%">
                      <MDTypography variant="h6" fontWeight="medium">
                        Average Profit Per Trade
                      </MDTypography>
                      <MDTypography variant="h4" fontWeight="bold">
                        ${metrics2.expectancy.toFixed(2)}
                      </MDTypography>
                    </MDBox>
                  </Card>
                </Grid>
              )}
{/* Trailing Stop Performance */}
<Grid item xs={12} md={selectionMode === 'single' ? 4 : 6}>
  <Card style={{ minHeight: '100px' }}>
    <MDBox display="flex" flexDirection="column" justifyContent="center" alignItems="center" pt={2} px={2} height="100%">
      <MDTypography variant="h6" fontWeight="medium">
        Trailing Stop Performance
      </MDTypography>
      <MDTypography variant="h4" fontWeight="bold">
        {metrics1.trailingStopStats.successRate.toFixed(1)}%
      </MDTypography>
      <MDTypography variant="caption">
        Used in {metrics1.trailingStopStats.used} trades
      </MDTypography>
    </MDBox>
  </Card>
</Grid>

{selectionMode === 'multiple' && metrics2 && (
  <Grid item xs={12} md={6}>
    <Card style={{ minHeight: '100px' }}>
      <MDBox display="flex" flexDirection="column" justifyContent="center" alignItems="center" pt={2} px={2} height="100%">
        <MDTypography variant="h6" fontWeight="medium">
          Trailing Stop Performance
        </MDTypography>
        <MDTypography variant="h4" fontWeight="bold">
          {metrics2.trailingStopStats.successRate.toFixed(1)}%
        </MDTypography>
        <MDTypography variant="caption">
          Used in {metrics2.trailingStopStats.used} trades
        </MDTypography>
      </MDBox>
    </Card>
  </Grid>
)}

{/* Partial Take Profit Performance */}
<Grid item xs={12} md={selectionMode === 'single' ? 4 : 6}>
  <Card style={{ minHeight: '100px' }}>
    <MDBox display="flex" flexDirection="column" justifyContent="center" alignItems="center" pt={2} px={2} height="100%">
      <MDTypography variant="h6" fontWeight="medium">
        Partial TP Performance
      </MDTypography>
      <MDTypography variant="h4" fontWeight="bold">
        {metrics1.partialTPStats.successRate.toFixed(1)}%
      </MDTypography>
      <MDTypography variant="caption">
        Used in {metrics1.partialTPStats.used} trades
      </MDTypography>
    </MDBox>
  </Card>
</Grid>

{selectionMode === 'multiple' && metrics2 && (
  <Grid item xs={12} md={6}>
    <Card style={{ minHeight: '100px' }}>
      <MDBox display="flex" flexDirection="column" justifyContent="center" alignItems="center" pt={2} px={2} height="100%">
        <MDTypography variant="h6" fontWeight="medium">
          Partial TP Performance
        </MDTypography>
        <MDTypography variant="h4" fontWeight="bold">
          {metrics2.partialTPStats.successRate.toFixed(1)}%
        </MDTypography>
        <MDTypography variant="caption">
          Used in {metrics2.partialTPStats.used} trades
        </MDTypography>
      </MDBox>
    </Card>
  </Grid>
)}

{/* Break Even Performance */}
<Grid item xs={12} md={selectionMode === 'single' ? 4 : 6}>
  <Card style={{ minHeight: '100px' }}>
    <MDBox display="flex" flexDirection="column" justifyContent="center" alignItems="center" pt={2} px={2} height="100%">
      <MDTypography variant="h6" fontWeight="medium">
        Break Even Performance
      </MDTypography>
      <MDTypography variant="h4" fontWeight="bold">
        {metrics1.breakEvenStats.successRate.toFixed(1)}%
      </MDTypography>
      <MDTypography variant="caption">
        Used in {metrics1.breakEvenStats.used} trades
      </MDTypography>
    </MDBox>
  </Card>
</Grid>

{selectionMode === 'multiple' && metrics2 && (
  <Grid item xs={12} md={6}>
    <Card style={{ minHeight: '100px' }}>
      <MDBox display="flex" flexDirection="column" justifyContent="center" alignItems="center" pt={2} px={2} height="100%">
        <MDTypography variant="h6" fontWeight="medium">
          Break Even Performance
        </MDTypography>
        <MDTypography variant="h4" fontWeight="bold">
          {metrics2.breakEvenStats.successRate.toFixed(1)}%
        </MDTypography>
        <MDTypography variant="caption">
          Used in {metrics2.breakEvenStats.used} trades
        </MDTypography>
      </MDBox>
    </Card>
  </Grid>
)}
              <Grid item xs={12} md={6}>
                <Card style={{ minHeight: '100px' }}>
                  <MDBox 
                    display="flex" 
                    flexDirection="column" 
                    justifyContent="center" 
                    alignItems="center" 
                    pt={2} 
                    px={2} 
                    height="100%"
                    width="100%"
                  >
                    <MDTypography variant="h6" fontWeight="medium">
                      Profit Factor
                    </MDTypography>
                    <MDTypography variant="h4" fontWeight="bold" mt={1}>
                      {metrics1.profitFactor.toFixed(2)}
                    </MDTypography>
                    <MDBox fontSize="92px" mt={1}>
                      {metrics1.profitFactor > 1.5 ? (
                        <TrendingUpIcon sx={{ color: '#5FB663', fontSize: 132 }} />
                      ) : metrics1.profitFactor > 1 ? (
                        <TrendingFlatIcon sx={{ color: '#FFA726', fontSize: 132 }} />
                      ) : (
                        <TrendingDownIcon sx={{ color: '#EA4643', fontSize: 132 }} />
                      )}
                    </MDBox>
                  </MDBox>
                </Card>
              </Grid>
              {selectionMode === 'multiple' && metrics2 && (
                <Grid item xs={12} md={6}>
                  <Card style={{ minHeight: '100px' }}>
                    <MDBox display="flex" flexDirection="column" justifyContent="center" alignItems="center" pt={2} px={2} height="100%">
                      <MDTypography variant="h6" fontWeight="medium">
                        Profit Factor
                      </MDTypography>
                      <MDTypography variant="h4" fontWeight="bold" mt={1}>
                        {metrics2.profitFactor.toFixed(2)}
                      </MDTypography>
                      <MDBox fontSize="92px" mt={1}>
                        {metrics2.profitFactor > 1.5 ? (
                          <TrendingUpIcon sx={{ color: '#5FB663', fontSize: 132 }} />
                        ) : metrics2.profitFactor > 1 ? (
                          <TrendingFlatIcon sx={{ color: '#FFA726', fontSize: 132 }} />
                        ) : (
                          <TrendingDownIcon sx={{ color: '#EA4643', fontSize: 132 }} />
                        )}
                      </MDBox>
                    </MDBox>
                  </Card>
                </Grid>
              )}
              <Grid item xs={12} md={6}>
                <Card style={{ minHeight: '100px' }}>
                  <MDBox display="flex" flexDirection="column" justifyContent="center" alignItems="center" pt={2} px={2} height="100%">
                    <MDTypography variant="h6" fontWeight="medium">
                      Win Rate
                    </MDTypography>
                    <MDBox display="flex" flexDirection="row" alignItems="center" justifyContent="center">
                      <MDBox mr={2}>
                        {[
                          { id: 'Wins', value: (metrics1.totalWins / metrics1.totalTrades) * 100, color: '#5FB663' },
                          { id: 'Losses', value: (metrics1.totalLosses / metrics1.totalTrades) * 100, color: '#EA4643' },
                          { id: 'Breakeven', value: (metrics1.totalBreakevens / metrics1.totalTrades) * 100, color: '#FFA726' }
                        ].map(item => (
                          <Box key={item.id} display="flex" alignItems="center" mb={1}>
                            <Box
                              width={16}
                              height={16}
                              sx={{ backgroundColor: item.color, borderRadius: '2px' }}
                              mr={1}
                            />
                            <MDTypography variant="body2" color="text" className="label-text-pie">
                              {item.id}: {item.value.toFixed(0)}%
                            </MDTypography>
                          </Box>
                        ))}
                      </MDBox>
                      <MDBox
                        style={{
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          width: 'auto', // Adjust width to ensure space for PieChart
                          height: 'auto'
                        }}
                      >
                        <ResponsiveContainer width={280} height={200}>
                          <PieChart>
                            <Pie
                              data={[
                                { name: 'Wins', value: metrics1.totalWins, color: '#5FB663' },
                                { name: 'Losses', value: metrics1.totalLosses, color: '#EA4643' },
                                { name: 'Breakeven', value: metrics1.totalBreakevens, color: '#FFA726' }
                              ]}
                              innerRadius={60}
                              outerRadius={80}
                              fill="#8884d8"
                              paddingAngle={5}
                              dataKey="value"
                              startAngle={90}
                              endAngle={450}
                            >
                              {[
                                { name: 'Wins', value: metrics1.totalWins, color: '#5FB663' },
                                { name: 'Losses', value: metrics1.totalLosses, color: '#EA4643' },
                                { name: 'Breakeven', value: metrics1.totalBreakevens, color: '#FFA726' }
                              ].map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={entry.color} />
                              ))}
                            </Pie>
                            <Tooltip />
                          </PieChart>
                        </ResponsiveContainer>
                      </MDBox>
                    </MDBox>
                  </MDBox>
                </Card>
              </Grid>
              {selectionMode === 'multiple' && metrics2 && (
                <Grid item xs={12} md={6}>
                  <Card style={{ minHeight: '100px' }}>
                    <MDBox display="flex" flexDirection="column" justifyContent="center" alignItems="center" pt={2} px={2} height="100%">
                      <MDTypography variant="h6" fontWeight="medium">
                        Win Rate
                      </MDTypography>
                      <MDBox display="flex" flexDirection="row" alignItems="center" justifyContent="center">
                        <MDBox mr={2}>
                          {[
                            { id: 'Wins', value: (metrics2.totalWins / metrics2.totalTrades) * 100, color: '#5FB663' },
                            { id: 'Losses', value: (metrics2.totalLosses / metrics2.totalTrades) * 100, color: '#EA4643' },
                            { id: 'Breakeven', value: (metrics2.totalBreakevens / metrics2.totalTrades) * 100, color: '#FFA726' }
                          ].map(item => (
                            <Box key={item.id} display="flex" alignItems="center" mb={1}>
                              <Box
                                width={16}
                                height={16}
                                sx={{ backgroundColor: item.color, borderRadius: '2px' }}
                                mr={1}
                              />
                              <MDTypography variant="body2" color="text" className="label-text-pie">
                                {item.id}: {item.value.toFixed(0)}%
                              </MDTypography>
                            </Box>
                          ))}
                        </MDBox>
                        <MDBox
                          style={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            width: 'auto', // Adjust width to ensure space for PieChart
                            height: 'auto'
                          }}
                        >
                          <ResponsiveContainer width={280} height={200}>
                            <PieChart>
                              <Pie
                                data={[
                                  { name: 'Wins', value: metrics2.totalWins, color: '#5FB663' },
                                  { name: 'Losses', value: metrics2.totalLosses, color: '#EA4643' },
                                  { name: 'Breakeven', value: metrics2.totalBreakevens, color: '#FFA726' }
                                ]}
                                innerRadius={60}
                                outerRadius={80}
                                fill="#8884d8"
                                paddingAngle={5}
                                dataKey="value"
                                startAngle={90}
                                endAngle={450}
                              >
                                {[
                                  { name: 'Wins', value: metrics2.totalWins, color: '#5FB663' },
                                  { name: 'Losses', value: metrics2.totalLosses, color: '#EA4643' },
                                  { name: 'Breakeven', value: metrics2.totalBreakevens, color: '#FFA726' }
                                ].map((entry, index) => (
                                  <Cell key={`cell-${index}`} fill={entry.color} />
                                ))}
                              </Pie>
                              <Tooltip />
                            </PieChart>
                          </ResponsiveContainer>
                        </MDBox>
                      </MDBox>
                    </MDBox>
                  </Card>
                </Grid>
              )}
                {(metrics1 && (selectionMode === 'single' || (selectionMode === 'multiple' && metrics2))) && (
                    <Grid item xs={12}>
                        <Card style={{ height: '300px' }}>
                            <MDBox display="flex" flexDirection="column" justifyContent="space-between" pt={1} px={6} height="100%">
                            <MDBox height="100%" width="100%">
                                <Line data={pnlChartData} options={pnlChartOptions} />
                            </MDBox>
                            </MDBox>
                        </Card>
                    </Grid>              
              )}
                {(metrics1 && (selectionMode === 'single' || (selectionMode === 'multiple' && metrics2))) && (
                    <Grid item xs={12}>
                        <Card style={{ height: '300px' }}>
                            <MDBox display="flex" flexDirection="column" justifyContent="space-between" pt={1} px={6} height="100%">
                            <MDBox height="100%" width="100%">
                                <Line data={riskChartData} options={riskChartOptions} />
                            </MDBox>
                            </MDBox>
                        </Card>
                    </Grid>              
              )} 
                {metrics1 && metrics2 && (
                <Modal
                open={openModal}
                onClose={handleCloseModal}
                aria-labelledby="comparison-report-modal"
                aria-describedby="comparison-report-description"
                sx={{ ...modalStyle, padding: '20px', borderRadius: '10px', width: '800px', height:'900px', backgroundColor: theme.palette.background.default || '#bac7db', }}
                >
                <Box sx={modalStyle}>
                    <MDBox display="flex" justifyContent="flex-end">
                    <IconButton onClick={handleCloseModal} sx={{ color: iconColor }}>
                        <CloseIcon />
                    </IconButton>
                    </MDBox>
                    <MDTypography id="comparison-report-modal" variant="h6" component="h2">
                    AI Enhanced Comparison Report
                    </MDTypography>
                    <MDBox mt={2}>
                    {generateReport(metrics1, metrics2)}
                    </MDBox>
                </Box>
                </Modal>    
        )}               
            </Grid>
          </MDBox>
        )}
      </MDBox>
    </Card>
  );
};

export default StrategyDropdown;